import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { SlideOverType } from './SlideOverType';
import { useSlideOver } from './hooks/useSlideOver';
import { SlideOverContext } from './context/slideOverContext';

export const SlideOverProvider = ({ children }: { children: React.ReactNode }) => {
    const [activePanel, setActivePanel] = useState<SlideOverType>(SlideOverType.NONE);

    const handlePanelChange = (panel: SlideOverType) => {
        if (panel === activePanel) {
            setActivePanel(SlideOverType.NONE);
        } else {
            setActivePanel(panel);
        }
    };

    return (
        <SlideOverContext.Provider value={{ activePanel, setActivePanel: handlePanelChange }}>
            {children}
        </SlideOverContext.Provider>
    );
};

interface SlideOverProps {
    type: SlideOverType;
    title: string;
    children: React.ReactNode;
    width?: string;
}

export const SlideOver = ({
    type,
    title,
    children,
    width = 'w-96'
}: SlideOverProps) => {
    const { activePanel, setActivePanel } = useSlideOver();
    const isOpen = activePanel === type;
    const [isVisible, setIsVisible] = useState(false);

    // Handle ESC key press
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                setActivePanel(SlideOverType.NONE);
            }
        };

        // Add event listener when component mounts or when isOpen changes
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        // Cleanup function to remove event listener
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, setActivePanel]);

    // Handle visibility state
    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
        } else {
            // When closing, wait for the transition to complete before hiding content
            const timer = setTimeout(() => {
                setIsVisible(false);
            }, 300); // This should match the duration in the transition class
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    return (
        <div
            className={`fixed inset-y-0 right-0 ${width} bg-white border-l border-gray-300 transform transition-transform duration-300 ease-in-out z-[49] ${isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
        >
            {(isVisible || isOpen) && (
                <div className="h-full flex flex-col relative w-full">
                    <div className="p-2 border-b border-gray-300 flex justify-between items-center">
                        <h2 className="text-xl font-bold text-gray-700">{title}</h2>
                        <button
                            onClick={() => setActivePanel(SlideOverType.NONE)}
                            className="hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X size={20} className="text-gray-600" />
                        </button>
                    </div>

                    <div className="flex-1 p-3 overflow-y-auto" style={{ minWidth: '700px', maxWidth: '700px' }}>
                        {children}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SlideOver;